import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Building2, FileText, Calendar, ChevronDown, RefreshCw, ExternalLink, Download, Mail } from "lucide-react";
import { Button } from "../components/ui/button";

const API = (window.location.origin.includes('localhost') 
  ? process.env.REACT_APP_BACKEND_URL 
  : window.location.origin) + "/api";

const PublicReport = () => {
  const { orgSlug, accountSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasHtmlReport, setHasHtmlReport] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Handle PDF download - generates complete report with Key Takeaways, same as email
  const handlePdfDownload = async () => {
    setDownloading(true);
    try {
      // Fetch the report HTML
      const response = await axios.get(getReportUrl(selectedReportId), { responseType: 'text' });
      const darkHtml = response.data;
      
      // Use the same generation logic as email copy
      const parser = new DOMParser();
      const doc = parser.parseFromString(darkHtml, 'text/html');
      
      const title = doc.querySelector('title')?.textContent || 'Performance Report';
      const subtitle = doc.querySelector('.subtitle')?.textContent || '';
      
      // Extract KPIs
      const kpiRows = doc.querySelectorAll('.kpi-row');
      const kpiGroups = [];
      kpiRows.forEach(row => {
        const cards = [];
        row.querySelectorAll('.kpi-card').forEach(card => {
          const label = card.querySelector('.label')?.textContent?.trim() || '';
          const value = card.querySelector('.value')?.textContent?.trim() || '';
          const sub = card.querySelector('.sub')?.textContent?.trim() || '';
          const colorClass = card.querySelector('.value')?.className || '';
          let color = '#1e293b';
          if (colorClass.includes('orange')) color = '#f97316';
          else if (colorClass.includes('green')) color = '#10b981';
          else if (colorClass.includes('blue')) color = '#3b82f6';
          else if (colorClass.includes('purple')) color = '#8b5cf6';
          else if (colorClass.includes('red')) color = '#ef4444';
          else if (colorClass.includes('teal')) color = '#14b8a6';
          if (label && value) cards.push({ label, value, sub, color });
        });
        if (cards.length > 0) kpiGroups.push(cards);
      });
      
      // Extract locations
      const locationRows = [];
      const locationsMatch = darkHtml.match(/const locations = (\[[\s\S]*?\]);/);
      if (locationsMatch && locationsMatch[1]) {
        try {
          const locationsData = JSON.parse(locationsMatch[1]);
          locationsData.forEach(loc => {
            const inboundNum = loc.inbound || 0;
            const connectedNum = loc.connected || 0;
            // Backend uses 'conversations' and 'voicemail' as full field names
            const conversationsNum = loc.conversations || loc.convos || 0;
            const voicemailNum = loc.voicemail || loc.vm || 0;
            const missedNum = inboundNum - connectedNum;
            const connectRate = inboundNum > 0 ? Math.round((connectedNum / inboundNum) * 100) : 0;
            const conversationRate = inboundNum > 0 ? Math.round((conversationsNum / inboundNum) * 100) : 0;
            const missedRate = inboundNum > 0 ? Math.round(((missedNum + voicemailNum) / inboundNum) * 100) : 0;
            // Backend provides appointment rates directly as percentages (e.g., 25 for 25%)
            // Check for both naming conventions: sales_appt_rate (backend) and salesAppt (old)
            const salesApptPct = loc.sales_appt_rate !== undefined ? loc.sales_appt_rate : Math.round((loc.salesAppt || 0) * 100);
            const serviceApptPct = loc.service_appt_rate !== undefined ? loc.service_appt_rate : Math.round((loc.svcAppt || 0) * 100);
            const partsOrderPct = loc.parts_appt_rate !== undefined ? loc.parts_appt_rate : Math.round((loc.partsAppt || 0) * 100);
            const score = (connectRate * 0.2) + (conversationRate * 0.3) + ((100 - missedRate) * 0.2) + (salesApptPct * 0.1) + (serviceApptPct * 0.1) + (partsOrderPct * 0.1);
            // Backend uses 'location' as the name field, not 'name'
            locationRows.push({ location: loc.location || loc.name, inbound: inboundNum.toLocaleString(), connected: connectedNum.toLocaleString(), conversations: conversationsNum.toLocaleString(), connectRate, conversationRate, missedRate, salesApptPct, serviceApptPct, partsOrderPct, score, inboundNum });
          });
        } catch (e) { console.error('Error parsing locations:', e); }
      }
      
      const rankedLocations = [...locationRows].sort((a, b) => b.score - a.score);
      
      // Generate takeaways (same as email)
      const generatePdfTakeaways = () => {
        if (rankedLocations.length < 3) return '';
        const topPerformers = rankedLocations.slice(0, 3);
        const solidPerformers = rankedLocations.slice(3, 9);
        const needsAttention = rankedLocations.filter(l => l.missedRate >= 40 && l.missedRate < 50);
        const critical = rankedLocations.filter(l => l.missedRate >= 50 || (l.salesApptPct === 0 && l.serviceApptPct === 0 && l.inboundNum > 50));
        const medals = ['🥇', '🥈', '🥉'];
        const badgeColors = [{ bg: '#fef3c7', border: '#f59e0b' }, { bg: '#f1f5f9', border: '#94a3b8' }, { bg: '#fed7aa', border: '#ea580c' }];
        
        let topHtml = topPerformers.map((loc, i) => {
          const strengths = [];
          if (loc.connectRate >= 90) strengths.push(`${loc.connectRate}% connected`);
          if (loc.conversationRate >= 60) strengths.push(`${loc.conversationRate}% conversations`);
          if (loc.serviceApptPct >= 40) strengths.push(`strong service ${loc.serviceApptPct}%`);
          if (loc.partsOrderPct >= 35) strengths.push(`strong parts ${loc.partsOrderPct}%`);
          const strengthText = strengths.length > 0 ? strengths.slice(0, 3).join(', ') : 'Consistent performer';
          const label = i === 0 ? '🟢 Best Overall' : i === 1 ? '🟢 Operationally Strong' : '🟢 Strong Performer';
          const risk = loc.salesApptPct === 0 ? 'Sales conversion 0%' : loc.salesApptPct <= 5 ? `Sales conversion ${loc.salesApptPct}%` : null;
          return `<div style="margin-bottom:10px;background:${badgeColors[i].bg};border-radius:10px;border-left:4px solid ${badgeColors[i].border};padding:14px 16px;"><span style="font-size:20px;">${medals[i]}</span> <strong style="font-size:16px;">${loc.location}</strong><br/><span style="color:#059669;font-size:11px;">${label}</span><br/><span style="font-size:12px;"><strong>Strength:</strong> ${strengthText}</span>${risk ? `<br/><span style="color:#dc2626;font-size:11px;">⚠️ Risk: ${risk}</span>` : ''}</div>`;
        }).join('');
        
        let solidHtml = solidPerformers.length > 0 ? `<p style="margin:20px 0 12px;font-weight:600;border-bottom:2px solid #22c55e;padding-bottom:8px;">✅ Solid Performers</p><table style="width:100%;border-collapse:collapse;">${solidPerformers.map((loc, i) => {
          const rank = i + 4;
          let note = loc.serviceApptPct >= 40 ? `Strong service (${loc.serviceApptPct}%)` : loc.partsOrderPct >= 35 ? `Strong parts (${loc.partsOrderPct}%)` : `Service ${loc.serviceApptPct}%`;
          return `<tr style="background:${i % 2 === 0 ? '#f0fdf4' : '#fff'};"><td style="padding:10px;font-weight:700;">#${rank}</td><td style="padding:10px;font-weight:600;">${loc.location}</td><td style="padding:10px;color:#059669;">🟡 Steady</td><td style="padding:10px;">${note}</td></tr>`;
        }).join('')}</table>` : '';
        
        let attentionHtml = needsAttention.length > 0 ? `<p style="margin:20px 0 12px;font-weight:600;border-bottom:2px solid #f59e0b;padding-bottom:8px;">⚠️ Needs Attention</p><table style="width:100%;border-collapse:collapse;">${needsAttention.map((loc, i) => {
          const rank = rankedLocations.indexOf(loc) + 1;
          return `<tr style="background:${i % 2 === 0 ? '#fef2f2' : '#fff'};"><td style="padding:10px;font-weight:700;">#${rank}</td><td style="padding:10px;font-weight:600;">${loc.location}</td><td style="padding:10px;color:#dc2626;">🔴 High Exposure</td><td style="padding:10px;color:#dc2626;">${loc.missedRate}% missed</td></tr>`;
        }).join('')}</table>` : '';
        
        const uniqueCritical = critical.filter(c => !needsAttention.some(n => n.location === c.location)).slice(0, 3);
        let criticalHtml = uniqueCritical.length > 0 ? `<p style="margin:20px 0 12px;color:#dc2626;font-weight:600;border-bottom:2px solid #dc2626;padding-bottom:8px;">🚨 Critical - Immediate Action Required</p>${uniqueCritical.map(loc => {
          const rank = rankedLocations.indexOf(loc) + 1;
          const issues = [];
          if (loc.missedRate >= 50) issues.push(`${loc.missedRate}% missed`);
          if (loc.salesApptPct === 0) issues.push('0% sales');
          if (loc.serviceApptPct === 0) issues.push('0% service');
          return `<div style="margin-bottom:10px;background:#fef2f2;border-radius:10px;border:2px solid #dc2626;padding:14px 16px;"><strong style="color:#7f1d1d;font-size:15px;">#${rank} ${loc.location}</strong><br/><span style="color:#dc2626;font-weight:600;">🚨 ${issues.join(' • ')}</span></div>`;
        }).join('')}` : '';
        
        return `<div style="padding:25px 20px 15px;"><h2 style="margin:0 0 15px;font-size:18px;">🏆 Location Performance Ranking</h2><p style="margin:0 0 15px;font-weight:600;border-bottom:2px solid #10b981;padding-bottom:8px;">🌟 Top Performers</p>${topHtml}${solidHtml}${attentionHtml}${criticalHtml}</div>`;
      };
      
      // Build metrics
      let metricsHtml = '';
      kpiGroups.forEach((group) => {
        metricsHtml += '<tr>' + group.map(m => {
          let bg = '#f1f5f9';
          if (m.label.toLowerCase().includes('missed')) bg = '#fef2f2';
          return `<td style="padding:6px;text-align:center;"><div style="background:${bg};border-radius:8px;padding:12px 8px;"><p style="margin:0;color:#64748b;font-size:9px;font-weight:600;">${m.label}</p><p style="margin:6px 0 0;color:${m.color};font-size:20px;font-weight:700;">${m.value}</p><p style="margin:3px 0 0;color:#94a3b8;font-size:9px;">${m.sub}</p></div></td>`;
        }).join('') + '</tr>';
      });
      
      // Build location table
      let locationTableHtml = locationRows.length > 0 ? `<div style="padding:20px 15px;"><p style="margin:0 0 10px;font-size:16px;font-weight:700;border-bottom:2px solid #8b5cf6;padding-bottom:10px;">📍 Complete Location Breakdown</p><table style="width:100%;border-collapse:collapse;"><tr style="background:#1e293b;color:#fff;"><td style="padding:10px 8px;font-size:10px;font-weight:600;">Location</td><td style="padding:10px 6px;font-size:10px;font-weight:600;text-align:center;">Inbound</td><td style="padding:10px 6px;font-size:10px;font-weight:600;text-align:center;">Connected</td><td style="padding:10px 6px;font-size:10px;font-weight:600;text-align:center;">Convos</td><td style="padding:10px 6px;font-size:10px;font-weight:600;text-align:center;">Missed %</td><td style="padding:10px 6px;font-size:10px;font-weight:600;text-align:center;">Sales %</td><td style="padding:10px 6px;font-size:10px;font-weight:600;text-align:center;">Service %</td></tr>${locationRows.map((loc, i) => `<tr style="background:${i % 2 === 0 ? '#fff' : '#f8fafc'};"><td style="padding:8px;font-weight:600;">${loc.location}</td><td style="padding:8px 6px;text-align:center;">${loc.inbound}</td><td style="padding:8px 6px;text-align:center;color:#10b981;font-weight:600;">${loc.connected}</td><td style="padding:8px 6px;text-align:center;color:#8b5cf6;">${loc.conversations}</td><td style="padding:8px 6px;text-align:center;color:${loc.missedRate >= 40 ? '#dc2626' : '#10b981'};font-weight:600;">${loc.missedRate}%</td><td style="padding:8px 6px;text-align:center;color:${loc.salesApptPct === 0 ? '#dc2626' : '#64748b'};font-weight:600;">${loc.salesApptPct}%</td><td style="padding:8px 6px;text-align:center;color:${loc.serviceApptPct >= 30 ? '#10b981' : '#64748b'};font-weight:600;">${loc.serviceApptPct}%</td></tr>`).join('')}</table></div>` : '';
      
      const accountName = accountInfo?.account?.name || accountSlug;
      const pdfHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${accountName} - ${subtitle}</title><style>@media print{body{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}}</style></head><body style="margin:0;padding:20px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;background:#fff;"><div style="max-width:720px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.1);"><div style="background:linear-gradient(135deg,#1e293b 0%,#334155 100%);padding:20px 25px;text-align:center;"><h1 style="margin:0;color:#f97316;font-size:22px;font-weight:700;">${accountName}</h1><p style="margin:8px 0 0;color:#94a3b8;font-size:13px;">${subtitle}</p></div>${generatePdfTakeaways()}<div style="padding:20px 10px;"><p style="margin:0 0 15px;padding:0 10px;font-size:16px;font-weight:700;border-bottom:2px solid #3b82f6;padding-bottom:10px;">📈 Performance Metrics</p><table style="width:100%;">${metricsHtml}</table></div>${locationTableHtml}<div style="background:#f1f5f9;padding:15px 25px;text-align:center;border-top:1px solid #e2e8f0;"><p style="margin:0;color:#64748b;font-size:11px;">Report generated by <strong>Calldrip</strong> Performance Analytics</p></div></div></body></html>`;
      
      // Open for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(pdfHtml);
        printWindow.document.close();
        const cleanName = accountName.replace(/[^a-zA-Z0-9]/g, '_');
        const currentReport = reports.find(r => r.id === selectedReportId);
        const periodClean = currentReport?.period?.replace(/\s+/g, '_') || 'Report';
        printWindow.document.title = `${cleanName}_${periodClean}_Report`;
        printWindow.onload = () => setTimeout(() => printWindow.print(), 500);
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Generate email-friendly light version of the report with Key Takeaways
  const handleEmailCopy = async () => {
    try {
      // First, try to fetch the uploaded email report
      try {
        const emailReportResponse = await axios.get(
          `${API}/external/reports/${orgSlug}/${accountSlug}/email-html`,
          { responseType: 'text' }
        );
        
        if (emailReportResponse.data) {
          // Use the uploaded email report - open it in a new window
          const newWindow = window.open('', '_blank');
          newWindow.document.write(emailReportResponse.data);
          newWindow.document.close();
          return;
        }
      } catch (e) {
        // No uploaded email report found, fall back to generating one
        console.log("No uploaded email report, generating from dashboard...");
      }
      
      // Fallback: Generate email from the dashboard report (old behavior)
      // Fetch the report HTML
      const response = await axios.get(getReportUrl(selectedReportId), { responseType: 'text' });
      const darkHtml = response.data;
      
      // Parse the HTML to extract key data
      const parser = new DOMParser();
      const doc = parser.parseFromString(darkHtml, 'text/html');
      
      // Extract title and subtitle
      const title = doc.querySelector('title')?.textContent || 'Performance Report';
      const subtitle = doc.querySelector('.subtitle')?.textContent || '';
      
      // Extract all KPI cards grouped by row
      const kpiRows = doc.querySelectorAll('.kpi-row');
      const kpiGroups = [];
      
      kpiRows.forEach(row => {
        const cards = [];
        row.querySelectorAll('.kpi-card').forEach(card => {
          const label = card.querySelector('.label')?.textContent?.trim() || '';
          const value = card.querySelector('.value')?.textContent?.trim() || '';
          const sub = card.querySelector('.sub')?.textContent?.trim() || '';
          const colorClass = card.querySelector('.value')?.className || '';
          
          let color = '#1e293b';
          if (colorClass.includes('orange')) color = '#f97316';
          else if (colorClass.includes('green')) color = '#10b981';
          else if (colorClass.includes('blue')) color = '#3b82f6';
          else if (colorClass.includes('purple')) color = '#8b5cf6';
          else if (colorClass.includes('red')) color = '#ef4444';
          else if (colorClass.includes('teal')) color = '#14b8a6';
          
          if (label && value) {
            cards.push({ label, value, sub, color });
          }
        });
        if (cards.length > 0) kpiGroups.push(cards);
      });
      
      // Extract location data from the JavaScript variable in the HTML
      // The report HTML contains: const locations = [{...}, {...}];
      const locationRows = [];
      const locationsMatch = darkHtml.match(/const locations = (\[[\s\S]*?\]);/);
      
      if (locationsMatch && locationsMatch[1]) {
        try {
          const locationsData = JSON.parse(locationsMatch[1]);
          
          locationsData.forEach(loc => {
            const inboundNum = loc.inbound || 0;
            const connectedNum = loc.connected || 0;
            // Backend uses 'conversations' and 'voicemail' as full field names
            const conversationsNum = loc.conversations || loc.convos || 0;
            const voicemailNum = loc.voicemail || loc.vm || 0;
            const missedNum = inboundNum - connectedNum;
            
            const connectRate = inboundNum > 0 ? Math.round((connectedNum / inboundNum) * 100) : 0;
            const conversationRate = inboundNum > 0 ? Math.round((conversationsNum / inboundNum) * 100) : 0;
            const missedRate = inboundNum > 0 ? Math.round(((missedNum + voicemailNum) / inboundNum) * 100) : 0;
            
            // Backend provides appointment rates directly as percentages (e.g., 25 for 25%)
            // Check for both naming conventions: sales_appt_rate (backend) and salesAppt (old)
            const salesApptPct = loc.sales_appt_rate !== undefined ? loc.sales_appt_rate : Math.round((loc.salesAppt || 0) * 100);
            const serviceApptPct = loc.service_appt_rate !== undefined ? loc.service_appt_rate : Math.round((loc.svcAppt || 0) * 100);
            const partsOrderPct = loc.parts_appt_rate !== undefined ? loc.parts_appt_rate : Math.round((loc.partsAppt || 0) * 100);
            
            // Calculate a composite score for ranking
            const score = (connectRate * 0.2) + (conversationRate * 0.3) + ((100 - missedRate) * 0.2) + 
                          (salesApptPct * 0.1) + (serviceApptPct * 0.1) + (partsOrderPct * 0.1);
            
            locationRows.push({ 
              // Backend uses 'location' as the name field, not 'name'
              location: loc.location || loc.name,
              inbound: inboundNum.toLocaleString(),
              connected: connectedNum.toLocaleString(),
              conversations: conversationsNum.toLocaleString(),
              voicemail: voicemailNum.toLocaleString(),
              missed: missedNum.toLocaleString(),
              salesAppt: `${salesApptPct}%`,
              serviceAppt: `${serviceApptPct}%`,
              partsOrder: `${partsOrderPct}%`,
              connectRate, conversationRate, missedRate,
              salesApptPct, serviceApptPct, partsOrderPct,
              score, inboundNum
            });
          });
        } catch (e) {
          console.error('Error parsing locations data:', e);
        }
      }
      
      // Sort locations by score for ranking
      const rankedLocations = [...locationRows].sort((a, b) => b.score - a.score);
      
      // Generate Key Takeaways analysis matching the report format
      const generateTakeaways = () => {
        if (rankedLocations.length < 3) return '';
        
        // Top 3 performers
        const topPerformers = rankedLocations.slice(0, 3);
        // Solid performers (ranks 4-9)
        const solidPerformers = rankedLocations.slice(3, 9);
        // Needs attention (high missed rates, not in critical)
        const needsAttention = rankedLocations.filter(l => l.missedRate >= 40 && l.missedRate < 50);
        // Critical (very high missed rate or zero appointments)
        const critical = rankedLocations.filter(l => l.missedRate >= 50 || (l.salesApptPct === 0 && l.serviceApptPct === 0 && l.inboundNum > 50));
        
        const medals = ['🥇', '🥈', '🥉'];
        const badgeColors = [
          { bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '#f59e0b', medal: '#f59e0b' },
          { bg: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', border: '#94a3b8', medal: '#94a3b8' },
          { bg: 'linear-gradient(135deg, #fef3e2 0%, #fed7aa 100%)', border: '#ea580c', medal: '#ea580c' }
        ];
        
        const getStrengthDescription = (loc, rank) => {
          const strengths = [];
          if (loc.connectRate >= 90) strengths.push(`${loc.connectRate}% connected`);
          if (loc.conversationRate >= 60) strengths.push(`${loc.conversationRate}% conversations`);
          if (loc.serviceApptPct >= 40) strengths.push(`strong service ${loc.serviceApptPct}%`);
          if (loc.partsOrderPct >= 35) strengths.push(`strong parts ${loc.partsOrderPct}%`);
          if (loc.missedRate <= 35) strengths.push(`low missed ${loc.missedRate}%`);
          
          const strengthText = strengths.length > 0 ? strengths.slice(0, 3).join(', ') : 'Consistent performer';
          
          let label = '🟢 Strong Performer';
          if (rank === 0) label = '🟢 Best Overall';
          else if (rank === 1) label = '🟢 Operationally Strong';
          else if (rank === 2 && loc.serviceApptPct >= 50) label = '🟢 Service Leader';
          
          return { label, strengthText };
        };
        
        const getRisk = (loc) => {
          if (loc.salesApptPct === 0) return `Sales conversion 0%`;
          if (loc.salesApptPct <= 5) return `Sales conversion ${loc.salesApptPct}%`;
          if (loc.missedRate >= 40) return `${loc.missedRate}% missed/voicemail`;
          return null;
        };
        
        // Build top performers section
        let topPerformersHtml = topPerformers.map((loc, i) => {
          const { label, strengthText } = getStrengthDescription(loc, i);
          const risk = getRisk(loc);
          return `
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 10px; background: ${badgeColors[i].bg}; border-radius: 10px; border-left: 4px solid ${badgeColors[i].border};">
            <tr>
              <td style="padding: 14px 16px;">
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td width="50" style="vertical-align: top;">
                      <div style="width: 40px; height: 40px; background: ${badgeColors[i].medal}; border-radius: 50%; text-align: center; line-height: 40px; font-size: 20px;">${medals[i]}</div>
                    </td>
                    <td style="vertical-align: top;">
                      <p style="margin: 0; font-size: 16px; font-weight: 700; color: #1e293b;">${loc.location}</p>
                      <p style="margin: 4px 0 0 0; font-size: 11px; color: #059669; font-weight: 600;">${label}</p>
                      <p style="margin: 6px 0 0 0; font-size: 12px; color: #57534e;"><strong>Strength:</strong> ${strengthText}</p>
                      ${risk ? `<p style="margin: 4px 0 0 0; font-size: 11px; color: #dc2626;">⚠️ Risk: ${risk}</p>` : ''}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>`;
        }).join('');
        
        // Build solid performers section
        let solidPerformersHtml = '';
        if (solidPerformers.length > 0) {
          solidPerformersHtml = `
            <p style="margin: 20px 0 12px 0; color: #1e293b; font-size: 14px; font-weight: 600; border-bottom: 2px solid #22c55e; padding-bottom: 8px;">✅ Solid Performers</p>
            <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
              ${solidPerformers.map((loc, i) => {
                const rank = i + 4;
                let status = '🟡 Steady';
                let note = '';
                if (loc.serviceApptPct >= 40) { status = '🟢 Consistent Performer'; note = `Strong service (${loc.serviceApptPct}%)`; }
                else if (loc.partsOrderPct >= 35) { status = '🟡 Balanced & Stable'; note = `Strong parts (${loc.partsOrderPct}%)`; }
                else if (loc.conversationRate >= 50) { status = '🟡 Good Engagement'; note = `${loc.conversationRate}% conversations`; }
                else { note = `Service ${loc.serviceApptPct}%`; }
                
                return `
                <tr style="background: ${i % 2 === 0 ? '#f0fdf4' : '#ffffff'}; border-bottom: 1px solid #dcfce7;">
                  <td style="padding: 10px 12px; width: 40px; font-weight: 700; color: #64748b; font-size: 12px;">#${rank}</td>
                  <td style="padding: 10px 8px; font-weight: 600; color: #1e293b; font-size: 13px;">${loc.location}</td>
                  <td style="padding: 10px 8px; font-size: 11px; color: #059669;">${status}</td>
                  <td style="padding: 10px 8px; font-size: 11px; color: #64748b;">${note}</td>
                </tr>`;
              }).join('')}
            </table>`;
        }
        
        // Build needs attention section
        let attentionHtml = '';
        if (needsAttention.length > 0) {
          attentionHtml = `
            <p style="margin: 20px 0 12px 0; color: #1e293b; font-size: 14px; font-weight: 600; border-bottom: 2px solid #f59e0b; padding-bottom: 8px;">⚠️ Needs Attention</p>
            <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
              ${needsAttention.map((loc, i) => {
                const rank = rankedLocations.indexOf(loc) + 1;
                return `
                <tr style="background: ${i % 2 === 0 ? '#fef2f2' : '#ffffff'}; border-bottom: 1px solid #fecaca;">
                  <td style="padding: 10px 12px; width: 40px; font-weight: 700; color: #64748b; font-size: 12px;">#${rank}</td>
                  <td style="padding: 10px 8px; font-weight: 600; color: #1e293b; font-size: 13px;">${loc.location}</td>
                  <td style="padding: 10px 8px; font-size: 11px; color: #dc2626;">🔴 ${loc.missedRate >= 45 ? 'High Exposure' : 'Volume Risk'}</td>
                  <td style="padding: 10px 8px; font-size: 11px; color: #dc2626;">${loc.missedRate}% missed/voicemail</td>
                </tr>`;
              }).join('')}
            </table>`;
        }
        
        // Build critical section
        let criticalHtml = '';
        const uniqueCritical = critical.filter(c => !needsAttention.some(n => n.location === c.location)).slice(0, 3);
        if (uniqueCritical.length > 0) {
          criticalHtml = `
            <p style="margin: 20px 0 12px 0; color: #dc2626; font-size: 14px; font-weight: 600; border-bottom: 2px solid #dc2626; padding-bottom: 8px;">🚨 Critical - Immediate Action Required</p>
            ${uniqueCritical.map(loc => {
              const rank = rankedLocations.indexOf(loc) + 1;
              let concern = '🔴 Highest Concern';
              if (loc.salesApptPct === 0 && loc.connectRate >= 90) concern = '🔴 Critical Sales Issue';
              
              const onlyStrength = loc.partsOrderPct >= 25 ? `Parts ${loc.partsOrderPct}%` : 
                                   loc.connectRate >= 85 ? `${loc.connectRate}% connected` : 'None identified';
              
              const issues = [];
              if (loc.missedRate >= 50) issues.push(`${loc.missedRate}% missed/voicemail`);
              if (loc.salesApptPct === 0) issues.push('0% sales appointments');
              if (loc.serviceApptPct === 0) issues.push('0% service appointments');
              
              return `
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 10px; background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%); border-radius: 10px; border: 2px solid #dc2626;">
                <tr>
                  <td style="padding: 14px 16px;">
                    <table width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td width="45" style="vertical-align: top;">
                          <div style="width: 36px; height: 36px; background: #dc2626; border-radius: 50%; text-align: center; line-height: 36px; color: white; font-weight: 700; font-size: 14px;">#${rank}</div>
                        </td>
                        <td style="vertical-align: top;">
                          <p style="margin: 0; font-size: 15px; font-weight: 700; color: #7f1d1d;">${loc.location}</p>
                          <p style="margin: 4px 0 0 0; font-size: 11px; color: #991b1b; font-weight: 600;">${concern}</p>
                          <p style="margin: 6px 0 0 0; font-size: 12px; color: #7f1d1d;"><strong>Only Strength:</strong> ${onlyStrength}</p>
                          <p style="margin: 6px 0 0 0; font-size: 12px; color: #dc2626; font-weight: 600;">🚨 ${issues.join(' • ')}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>`;
            }).join('')}`;
        }
        
        return `
          <tr>
            <td style="padding: 25px 20px 15px 20px;">
              <h2 style="margin: 0 0 15px 0; color: #1e293b; font-size: 18px; font-weight: 700;">🏆 Location Performance Ranking</h2>
              <p style="margin: 0 0 15px 0; color: #1e293b; font-size: 14px; font-weight: 600; border-bottom: 2px solid #10b981; padding-bottom: 8px;">🌟 Top Performers</p>
              ${topPerformersHtml}
              ${solidPerformersHtml}
              ${attentionHtml}
              ${criticalHtml}
            </td>
          </tr>`;
      };
      
      // Build metrics HTML
      const buildMetricCard = (metric, bgColor = '#f1f5f9') => `
        <td width="25%" style="padding: 6px; text-align: center; vertical-align: top;">
          <div style="background: ${bgColor}; border-radius: 8px; padding: 12px 8px; border: 1px solid #e2e8f0;">
            <p style="margin: 0; color: #64748b; font-size: 9px; font-weight: 600; text-transform: uppercase;">${metric.label}</p>
            <p style="margin: 6px 0 0 0; color: ${metric.color}; font-size: 20px; font-weight: 700;">${metric.value}</p>
            <p style="margin: 3px 0 0 0; color: #94a3b8; font-size: 9px;">${metric.sub}</p>
          </div>
        </td>`;
      
      let metricsHtml = '';
      kpiGroups.forEach((group, groupIdx) => {
        if (groupIdx === 2 && group.some(m => m.label.toLowerCase().includes('website') || m.label.toLowerCase().includes('gmb'))) {
          metricsHtml += `<tr><td colspan="4" style="padding: 15px 8px 5px 8px;"><p style="margin: 0; color: #64748b; font-size: 11px; font-weight: 600; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">Call Source Breakdown</p></td></tr>`;
        }
        const rowHtml = group.map((m) => {
          let bg = '#f1f5f9';
          if (m.label.toLowerCase().includes('missed')) bg = '#fef2f2';
          if (m.label.toLowerCase().includes('busiest')) bg = '#fef3c7';
          if (m.label.toLowerCase().includes('gmb')) bg = '#f0fdf4';
          if (m.label.toLowerCase().includes('website') || m.label.toLowerCase().includes('web ')) bg = '#eff6ff';
          return buildMetricCard(m, bg);
        }).join('');
        metricsHtml += `<tr>${rowHtml}</tr>`;
      });
      
      // Generate key takeaways section
      const keyTakeawaysHtml = generateTakeaways();
      
      // Build FULL location table - ALL locations, not just top 8
      let locationTableHtml = '';
      if (locationRows.length > 0) {
        locationTableHtml = `
          <tr>
            <td style="padding: 20px 15px 10px 15px;">
              <p style="margin: 0 0 10px 0; color: #1e293b; font-size: 16px; font-weight: 700; border-bottom: 2px solid #8b5cf6; padding-bottom: 10px;">📍 Complete Location Breakdown</p>
              <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse;">
                <tr style="background: #1e293b;">
                  <td style="padding: 10px 8px; font-size: 10px; font-weight: 600; color: #ffffff; text-align: left; border-bottom: 1px solid #334155;">Location</td>
                  <td style="padding: 10px 6px; font-size: 10px; font-weight: 600; color: #ffffff; text-align: center; border-bottom: 1px solid #334155;">Inbound</td>
                  <td style="padding: 10px 6px; font-size: 10px; font-weight: 600; color: #ffffff; text-align: center; border-bottom: 1px solid #334155;">Connected</td>
                  <td style="padding: 10px 6px; font-size: 10px; font-weight: 600; color: #ffffff; text-align: center; border-bottom: 1px solid #334155;">Convos</td>
                  <td style="padding: 10px 6px; font-size: 10px; font-weight: 600; color: #ffffff; text-align: center; border-bottom: 1px solid #334155;">Missed %</td>
                  <td style="padding: 10px 6px; font-size: 10px; font-weight: 600; color: #ffffff; text-align: center; border-bottom: 1px solid #334155;">Sales %</td>
                  <td style="padding: 10px 6px; font-size: 10px; font-weight: 600; color: #ffffff; text-align: center; border-bottom: 1px solid #334155;">Service %</td>
                </tr>
                ${locationRows.map((loc, i) => {
                  const missedColor = loc.missedRate >= 40 ? '#dc2626' : loc.missedRate >= 30 ? '#f59e0b' : '#10b981';
                  const salesColor = loc.salesApptPct === 0 ? '#dc2626' : loc.salesApptPct >= 20 ? '#10b981' : '#64748b';
                  const serviceColor = loc.serviceApptPct >= 30 ? '#10b981' : '#64748b';
                  return `
                  <tr style="background: ${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                    <td style="padding: 8px; font-size: 11px; font-weight: 600; color: #1e293b; border-bottom: 1px solid #e2e8f0;">${loc.location}</td>
                    <td style="padding: 8px 6px; font-size: 11px; color: #64748b; text-align: center; border-bottom: 1px solid #e2e8f0;">${loc.inbound}</td>
                    <td style="padding: 8px 6px; font-size: 11px; color: #10b981; font-weight: 600; text-align: center; border-bottom: 1px solid #e2e8f0;">${loc.connected}</td>
                    <td style="padding: 8px 6px; font-size: 11px; color: #8b5cf6; text-align: center; border-bottom: 1px solid #e2e8f0;">${loc.conversations}</td>
                    <td style="padding: 8px 6px; font-size: 11px; color: ${missedColor}; font-weight: 600; text-align: center; border-bottom: 1px solid #e2e8f0;">${loc.missedRate}%</td>
                    <td style="padding: 8px 6px; font-size: 11px; color: ${salesColor}; font-weight: 600; text-align: center; border-bottom: 1px solid #e2e8f0;">${loc.salesApptPct}%</td>
                    <td style="padding: 8px 6px; font-size: 11px; color: ${serviceColor}; font-weight: 600; text-align: center; border-bottom: 1px solid #e2e8f0;">${loc.serviceApptPct}%</td>
                  </tr>`;
                }).join('')}
              </table>
            </td>
          </tr>`;
      }
      
      const emailHtml = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><title>${title}</title></head>
<body style="margin: 0; padding: 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #f8fafc;">
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 720px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
    <tr>
      <td style="background: linear-gradient(135deg, #1e293b 0%, #334155 100%); padding: 20px 25px; text-align: center;">
        <h1 style="margin: 0; color: #f97316; font-size: 22px; font-weight: 700;">${title.split(' - ')[0] || title}</h1>
        <p style="margin: 8px 0 0 0; color: #94a3b8; font-size: 13px;">${subtitle}</p>
      </td>
    </tr>
    ${keyTakeawaysHtml}
    <tr>
      <td style="padding: 20px 10px 10px 10px;">
        <p style="margin: 0 0 15px 0; padding: 0 10px; color: #1e293b; font-size: 16px; font-weight: 700; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">📈 Performance Metrics</p>
        <table width="100%" cellpadding="0" cellspacing="0">${metricsHtml}</table>
      </td>
    </tr>
    ${locationTableHtml}
    <tr>
      <td style="padding: 25px; text-align: center;">
        <p style="margin: 0 0 15px 0; color: #64748b; font-size: 13px;">View the interactive dashboard with charts and detailed breakdowns:</p>
        <a href="https://app.calldrip.studio/reports/${orgSlug}/${accountSlug}" style="display: inline-block; background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); color: #ffffff; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-weight: 700; font-size: 14px; box-shadow: 0 4px 12px rgba(249,115,22,0.3);">📊 View Full Interactive Report →</a>
      </td>
    </tr>
    <tr>
      <td style="background: #f1f5f9; padding: 15px 25px; text-align: center; border-top: 1px solid #e2e8f0;">
        <p style="margin: 0; color: #64748b; font-size: 11px;">Report generated by <strong style="color: #1e293b;">Calldrip</strong> • Questions? Reply to this email</p>
      </td>
    </tr>
  </table>
</body>
</html>`;
      
      const newWindow = window.open('', '_blank');
      newWindow.document.write(emailHtml);
      newWindow.document.close();
    } catch (error) {
      console.error('Error generating email version:', error);
      alert('Failed to generate email version. Please try again.');
    }
  };

  useEffect(() => {
    checkForReports();
  }, [orgSlug, accountSlug]);

  const checkForReports = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First check if there are uploaded HTML reports
      const listResponse = await axios.get(`${API}/external/reports/${orgSlug}/${accountSlug}/list`);
      
      if (listResponse.data.reports && listResponse.data.reports.length > 0) {
        setReports(listResponse.data.reports);
        setHasHtmlReport(true);
        
        // Get account info from the first report or API
        const currentReport = listResponse.data.reports.find(r => r.is_current) || listResponse.data.reports[0];
        setSelectedReportId(currentReport.id);
        
        // Try to get org/account info, but don't fail if it doesn't work
        try {
          const infoResponse = await axios.get(`${API}/external/reports/${orgSlug}/${accountSlug}`);
          setAccountInfo(infoResponse.data);
        } catch (e) {
          // If no structured data, create info from the report or slug
          const report = currentReport;
          setAccountInfo({
            organization: { 
              name: report.org_name || orgSlug.replace(/corporate/i, ' Corporate').replace(/kubota/i, 'Kubota').trim(), 
              slug: orgSlug 
            },
            account: { 
              name: report.account_name || accountSlug.replace(/equipment/i, ' Equipment').replace(/floridacoast/i, 'Florida Coast').trim(), 
              slug: accountSlug 
            }
          });
        }
        return; // Successfully found reports, exit early
      }
      
      // No HTML reports found - try the structured data endpoint
      try {
        const dataResponse = await axios.get(`${API}/external/reports/${orgSlug}/${accountSlug}`);
        setAccountInfo(dataResponse.data);
        setHasHtmlReport(false);
      } catch (e) {
        // Neither reports nor org/account found
        setError("Report not found");
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(err.response?.data?.detail || "Report not found");
    } finally {
      setLoading(false);
    }
  };

  const getReportUrl = (reportId = null) => {
    const base = `${API}/external/reports/${orgSlug}/${accountSlug}/html`;
    return reportId ? `${base}?report_id=${encodeURIComponent(reportId)}` : base;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-t-indigo-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 size={32} className="text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Report Not Found</h1>
          <p className="text-slate-400 mb-4">{error}</p>
          <p className="text-slate-500 text-sm">
            Check the URL or contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // If we have HTML reports, show them in an iframe
  if (hasHtmlReport) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        {/* Header Bar */}
        <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <img 
              src="https://www.calldrip.com/hs-fs/hubfs/calldrip-logo-white.png?width=280&height=70&name=calldrip-logo-white.png" 
              alt="Calldrip" 
              className="h-8 w-auto"
            />
            <div className="h-6 w-px bg-slate-600"></div>
            <div>
              <h1 className="text-white font-semibold">
                {accountInfo?.account?.name || accountSlug}
              </h1>
              <p className="text-slate-400 text-xs">
                {accountInfo?.organization?.name || orgSlug}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Report Selector */}
            {reports.length > 1 && (
              <div className="relative">
                <select
                  value={selectedReportId || ""}
                  onChange={(e) => setSelectedReportId(e.target.value)}
                  className="appearance-none bg-slate-700 text-white text-sm rounded-lg px-4 py-2 pr-8 border border-slate-600 focus:outline-none focus:border-indigo-500"
                >
                  {reports.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.title}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            )}
            
            {/* Download PDF - downloads HTML and opens print dialog */}
            <button
              onClick={handlePdfDownload}
              disabled={downloading}
              className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white text-sm px-4 py-2 rounded-lg transition-colors border border-slate-600 disabled:opacity-50"
              title="Download as PDF"
            >
              <Download size={16} />
              {downloading ? 'Downloading...' : 'PDF'}
            </button>
            
            {/* Email-friendly version */}
            <button
              onClick={handleEmailCopy}
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              title="Open email-friendly version"
            >
              <Mail size={16} />
              Email Copy
            </button>
            
            {/* Open in New Tab */}
            <a
              href={getReportUrl(selectedReportId)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              <ExternalLink size={16} />
              Full Screen
            </a>
          </div>
        </header>
        
        {/* Report iframe */}
        <div className="flex-1">
          <iframe
            src={getReportUrl(selectedReportId)}
            className="w-full h-full border-0"
            title={`${accountSlug} Report`}
            style={{ minHeight: "calc(100vh - 60px)" }}
          />
        </div>
      </div>
    );
  }

  // Fallback: No HTML reports, show basic info
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText size={32} className="text-indigo-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">
          {accountInfo?.account?.name || accountSlug}
        </h1>
        <p className="text-slate-400 mb-6">
          {accountInfo?.organization?.name || orgSlug}
        </p>
        
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 text-left">
          <h3 className="text-white font-semibold mb-3">No Reports Available Yet</h3>
          <p className="text-slate-400 text-sm mb-4">
            Reports will appear here once they've been uploaded by your administrator.
          </p>
          <div className="bg-slate-900 rounded-lg p-3">
            <p className="text-slate-500 text-xs mb-1">Account Details:</p>
            <code className="text-indigo-400 text-xs">
              Organization: {orgSlug}<br/>
              Account: {accountSlug}
            </code>
          </div>
        </div>
        
        <Button
          onClick={checkForReports}
          variant="outline"
          className="mt-6 border-slate-600 hover:bg-slate-800"
        >
          <RefreshCw size={16} className="mr-2" />
          Check Again
        </Button>
      </div>
    </div>
  );
};

export default PublicReport;
